'use client';

import Link from 'next/link';
import Image from 'next/image';
import Icon from '@/components/ui/AppIcon';
import { tools } from '@/config/tools';

const companyLinks = [
  { label: 'Privacy Policy', href: '/privacy-policy', icon: 'ShieldCheckIcon' },
  { label: 'Terms & Conditions', href: '/terms-and-conditions', icon: 'DocumentTextIcon' },
  { label: 'Disclaimer', href: '/disclaimer', icon: 'ExclamationTriangleIcon' },
  { label: 'DMCA Policy', href: '/dmca-policy', icon: 'NoSymbolIcon' },
  { label: 'Cookie Policy', href: '/cookie-policy', icon: 'CookieIcon' },
  { label: 'Blog', href: '/blog', icon: 'BookOpenIcon' },
  { label: 'About Us', href: '/about', icon: 'UserGroupIcon' },
  { label: 'Contact Us', href: '/contact', icon: 'EnvelopeIcon' },
];

const socialLinks = [
  {
    name: 'X (Twitter)',
    href: 'https://x.com/LoveUpdf',
    label: 'Follow LoveUPDF on X (formerly Twitter)',
    svg: (
      <svg
        className="w-4 h-4"
        fill="currentColor"
        viewBox="0 0 24 24"
        aria-hidden="true"
        focusable="false"
      >
        <path d="M18.901 1.153h3.68l-8.04 9.19L24 22.846h-7.406l-5.8-7.584-6.638 7.584H.474l8.6-9.83L0 1.154h7.594l5.243 6.932ZM17.61 20.644h2.039L6.486 3.24H4.298Z" />
      </svg>
    ),
  },
  {
    name: 'LinkedIn',
    href: 'https://www.linkedin.com/company/loveupdf/',
    label: 'Follow LoveUPDF on LinkedIn',
    svg: (
      <svg
        className="w-4 h-4"
        fill="currentColor"
        viewBox="0 0 24 24"
        aria-hidden="true"
        focusable="false"
      >
        <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.454C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
      </svg>
    ),
  },
  {
    name: 'Facebook',
    href: 'https://www.facebook.com/people/LoveUpdf/61579682636933/',
    label: 'Follow LoveUPDF on Facebook',
    svg: (
      <svg
        className="w-4 h-4"
        fill="currentColor"
        viewBox="0 0 24 24"
        aria-hidden="true"
        focusable="false"
      >
        <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
      </svg>
    ),
  },
  {
    name: 'Reddit',
    href: 'https://www.reddit.com/user/loveupdf/',
    label: 'Join LoveUPDF on Reddit',
    svg: (
      <svg
        className="w-4 h-4"
        fill="currentColor"
        viewBox="0 0 24 24"
        aria-hidden="true"
        focusable="false"
      >
        <path d="M12 0C5.373 0 0 5.373 0 12c0 6.627 5.373 12 12 12s12-5.373 12-12C24 5.373 18.627 0 12 0zm5.894 13.688c.044.295.044.594 0 .888C17.894 17.15 15.21 19 12 19s-5.894-1.85-5.894-4.424a3.88 3.88 0 010-.888 1.894 1.894 0 01-.61-3.488 1.9 1.9 0 012.57.73 9.29 9.29 0 015.014-1.567l.952-4.437a.374.374 0 01.448-.285l3.121.668a1.313 1.313 0 111.265 1.594 1.315 1.315 0 01-1.257-1.665l-2.77-.591-.838 3.915a9.285 9.285 0 014.997 1.57 1.9 1.9 0 012.57-.73 1.894 1.894 0 01-.609 3.488zm-8.706-.563a1.25 1.25 0 100 2.5 1.25 1.25 0 000-2.5zm5.624 0a1.25 1.25 0 100 2.5 1.25 1.25 0 000-2.5zm-2.812 3.875c-.769 0-1.625-.313-2.188-.875a.376.376 0 01.532-.531c.387.387 1.012.656 1.656.656s1.269-.269 1.656-.656a.375.375 0 01.532.531c-.563.563-1.419.875-2.188.875z" />
      </svg>
    ),
  },
  {
    name: 'Instagram',
    href: 'https://www.instagram.com/loveupdf/',
    label: 'Follow LoveUPDF on Instagram',
    svg: (
      <svg
        className="w-4 h-4"
        fill="currentColor"
        viewBox="0 0 24 24"
        aria-hidden="true"
        focusable="false"
      >
        <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zM5.838 12a6.162 6.162 0 1112.324 0 6.162 6.162 0 01-12.324 0zM12 16a4 4 0 110-8 4 4 0 010 8zm4.965-10.405a1.44 1.44 0 112.881.001 1.44 1.44 0 01-2.881-.001z" />
      </svg>
    ),
  },
];

export default function Footer() {
  return (
    <footer
      className="border-t bg-zinc-50 border-brand-border"
      role="contentinfo"
      aria-label="Site footer"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-10 lg:py-14">
        {/* ── Top grid ─────────────────────────────────────────────────────── */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-12 gap-8 lg:gap-10">
          {/* Brand column — 4 cols */}
          <div className="sm:col-span-2 lg:col-span-4 flex flex-col items-start gap-4">
            <Link
              href="/"
              className="flex items-center gap-1 group focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 rounded-lg"
              aria-label="LoveUPDF — go to homepage"
            >
              <Image
                src="/assets/images/app_logo.png"
                alt=""
                width={80}
                height={80}
                className="w-14 h-14 md:w-16 md:h-16 object-contain transition-transform group-hover:scale-105"
                loading="lazy"
              />
              <span className="font-heading font-extrabold text-3xl text-brand-dark leading-none">
                LoveU
                <span className="bg-[linear-gradient(135deg,#E8445A,#FF7A8A)] bg-clip-text text-transparent">
                  PDF
                </span>
              </span>
            </Link>

            {/* Brand description — visible, crawlable, no hidden blocks */}
            <p className="text-sm text-brand-muted leading-relaxed max-w-[260px]">
              Free, privacy-first PDF tools. Zero file storage, no account needed, 100% free
              forever.
            </p>

            {/* Trust badge */}
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-primary-light border border-primary-mid">
              <Icon
                name="LockClosedIcon"
                size={11}
                variant="solid"
                className="text-primary"
                aria-hidden="true"
              />
              <span className="text-primary text-[11px] font-heading font-bold tracking-[0.05em] uppercase">
                256-bit SSL · Auto-Deleted
              </span>
            </div>
          </div>

          {/* Tools column — 4 cols */}
          <div className="lg:col-span-4">
            <h3 className="text-xs font-semibold uppercase tracking-wider text-brand-dark font-heading mb-4">
              PDF Tools
            </h3>
            <ul className="grid grid-cols-2 gap-x-4 gap-y-2" role="list">
              {tools.map((tool) => (
                <li key={tool.id}>
                  <Link
                    href={tool.href}
                    className="flex items-center gap-2 text-sm text-brand-muted hover:text-primary transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary rounded-md py-0.5 font-body group"
                  >
                    <Icon
                      name={tool.icon as any}
                      size={13}
                      className="flex-shrink-0 text-brand-muted group-hover:text-primary transition-colors"
                      aria-hidden="true"
                    />
                    <span>{tool.title}</span>
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Company column — 4 cols */}
          <div className="lg:col-span-4">
            {/* FIX: was h4 while adjacent column used h3 — consistent heading hierarchy now */}
            <h3 className="text-xs font-semibold uppercase tracking-wider text-brand-dark font-heading mb-4">
              Company
            </h3>
            <ul className="grid grid-cols-2 gap-x-4 gap-y-2" role="list">
              {companyLinks.map((link) => (
                <li key={link.href}>
                  <Link
                    href={link.href}
                    className="flex items-center gap-2 text-sm text-brand-muted hover:text-primary transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary rounded-md py-0.5 font-body group"
                  >
                    <Icon
                      name={link.icon as any}
                      size={13}
                      className="flex-shrink-0 text-brand-muted group-hover:text-primary transition-colors"
                      aria-hidden="true"
                    />
                    <span>{link.label}</span>
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* ── Bottom bar ───────────────────────────────────────────────────── */}
        <div className="mt-10 pt-6 border-t border-brand-border">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-5">
            {/* Copyright + socials */}
            <div className="flex flex-col sm:flex-row items-center gap-4">
              <p className="text-xs text-brand-muted font-body order-2 sm:order-1">
                © <span suppressHydrationWarning>{new Date().getFullYear()}</span> LoveUPDF. All
                rights reserved.
              </p>

              {/* Social links */}
              <ul
                className="flex items-center gap-2 order-1 sm:order-2"
                aria-label="LoveUPDF on social media"
                role="list"
              >
                {socialLinks.map((social) => (
                  <li key={social.name}>
                    <a
                      href={social.href}
                      target="_blank"
                      rel="noopener noreferrer nofollow"
                      className="w-8 h-8 flex items-center justify-center rounded-full bg-white border border-brand-border text-brand-muted hover:text-primary hover:border-primary-mid hover:bg-primary-light transition-all hover:scale-110 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2"
                      aria-label={social.label}
                    >
                      {social.svg}
                    </a>
                  </li>
                ))}
              </ul>
            </div>

            {/* Trust badges */}
            <div className="flex items-center gap-4 flex-wrap justify-center sm:justify-end">
              <span className="text-xs flex items-center gap-1.5 text-brand-muted">
                <Icon
                  name="ShieldCheckIcon"
                  size={12}
                  className="text-primary"
                  aria-hidden="true"
                />
                Privacy First
              </span>
              <span className="text-xs flex items-center gap-1.5 text-brand-muted">
                <Icon name="TrashIcon" size={12} className="text-primary" aria-hidden="true" />
                Zero Storage
              </span>
              <span className="text-xs flex items-center gap-1.5 text-brand-muted">
                <Icon
                  name="CurrencyDollarIcon"
                  size={12}
                  className="text-primary"
                  aria-hidden="true"
                />
                100% Free
              </span>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}
